// javascript version of com\esko\utl\crypto\secdc.java

const _defaultKey: number = 45678;

function unsigned (charNumber: number): number {
  return (charNumber + 0x100) & 0xFF;
}

function nextKey (key: number, value: number): number {
  let ecx = key;
  let eax = unsigned(value);
  eax += ecx;
  ecx = eax;
  ecx = ecx << 5;
  ecx += eax;
  ecx = ecx + ecx * 4;
  ecx = eax + ecx * 8;
  eax = eax + ecx * 8;
  ecx = eax + eax * 4 + 0x58BF;

  return ecx & 0xFFFF;
}

function scrambleWithKey (input: string, key: number): string {
  let output: string = '';
  for (let i = 0; i < input.length; i++) {
    const inputChar: number = input.charCodeAt(i);
    const value: number = unsigned(inputChar);
    const encryptedValue: number = (value ^ (key >> 8)) & 0xFF;

    output += String.fromCharCode(encryptedValue);

    key = nextKey(key, encryptedValue);
  }
  return output;
}

function descrambleWithKey (input: string, key: number): string {
  let output: string = '';
  for (let i = 0; i < input.length; i++) {
    const inputChar: number = input.charCodeAt(i);
    const decryptedValue: number = (inputChar ^ ((key >> 8) & 0xFF)) & 0xFF;

    output += String.fromCharCode(decryptedValue);

    key = nextKey(key, inputChar);
  }
  return output;
}

function scramble (input: string): string {
  return scrambleWithKey(input, _defaultKey);
}

function descramble (input: string): string {
  return descrambleWithKey(input, _defaultKey);
}

const _0: number = '0'.charCodeAt(0);
const _9: number = '9'.charCodeAt(0);
const _A: number = 'A'.charCodeAt(0);
const _F: number = 'F'.charCodeAt(0);
const _a: number = 'a'.charCodeAt(0);
const _f: number = 'f'.charCodeAt(0);

function toHexNumber (value: number): number {
  if (value >= _0 && value <= _9) {
    return value - _0;
  } else if (value >= _A && value <= _F) {
    return value - _A + 10;
  } else if (value >= _a && value <= _f) {
    return value - _a + 10;
  }
  return 0;
}

function fromHexNumber (value: number): number {
  if (value < 10) {
    return _0 + value;
  } else if (value < 16) {
    return _A + (value - 10);
  }
  return 0;
}

function hexEncode (input: string): string {
  let output = '';

  for (let i = 0; i < input.length; i++) {
    const val: number = unsigned(input.charCodeAt(i));
    output += String.fromCharCode(fromHexNumber((val / 16) & 0xFF)) + String.fromCharCode(fromHexNumber((val % 16) & 0xFF));
  }

  return output;
}

function hexDecode (input: string): string {
  let output: string = '';
  for (let i = 0; i < input.length; i += 2) {
    const hexNumber: number = toHexNumber(input.charCodeAt(i)) * 16 + toHexNumber(input.charCodeAt(i + 1));
    output += String.fromCharCode(hexNumber);
  }

  return output;
}

function safeIdEncode (id: string): string {
  const escapedId: string = encodeURIComponent(id);

  let base64String: string = btoa(escapedId);
  const length: number = base64String.length;
  base64String = base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const sizeDif = length - base64String.length;
  return base64String + sizeDif;
}

function safeIdDecode (safeId: string): string {
  const lastChar: string = safeId.substring(safeId.length - 1);
  let base64String: string = safeId.substring(0, safeId.length - 1);

  if (lastChar === '1') {
    base64String += '=';
  } else if (lastChar === '2') {
    base64String += '==';
  }

  base64String = base64String.replace(/-/g, '+').replace(/_/g, '/');

  const decoded: string = atob(base64String);
  const toReturn: string = decodeURIComponent(decoded);

  return toReturn;
}

function normalizeHexChars (input: string): string {
  const output: string = input.replace(/%[A-F0-9][A-F0-9]/g, function (str) {
    return str.toLowerCase();
  });
  return output;
}

function toUTF8 (str: string): string {
  let utf8String = '';
  for (let i = 0; i < str.length; i++) {
    let charcode = str.charCodeAt(i);
    if (charcode < 0x80) {
      utf8String += String.fromCharCode(charcode);
    } else if (charcode < 0x800) {
      utf8String += String.fromCharCode(0xc0 | (charcode >> 6));
      utf8String += String.fromCharCode(0x80 | (charcode & 0x3f));
    } else if (charcode < 0xd800 || charcode >= 0xe000) {
      utf8String += String.fromCharCode(0xe0 | (charcode >> 12));
      utf8String += String.fromCharCode(0x80 | ((charcode >> 6) & 0x3f));
      utf8String += String.fromCharCode(0x80 | (charcode & 0x3f));
    } else { // surrogate pair
      i++;
          // UTF-16 encodes 0x10000-0x10FFFF by
          // subtracting 0x10000 and splitting the
          // 20 bits of 0x0-0xFFFFF into two halves
      charcode = 0x10000 + (((charcode & 0x3ff) << 10) |
                    (str.charCodeAt(i) & 0x3ff));
      utf8String += String.fromCharCode(0xf0 | (charcode >> 18));
      utf8String += String.fromCharCode(0x80 | ((charcode >> 12) & 0x3f));
      utf8String += String.fromCharCode(0x80 | ((charcode >> 6) & 0x3f));
      utf8String += String.fromCharCode(0x80 | (charcode & 0x3f));
    }
  }
  return utf8String;
}

function fromUTF8 (utf8String: string): string {
  let data = [];
  for (let i = 0; i < utf8String.length; i++) {
    data.push(utf8String.charCodeAt(i));
  }

  let str = '';
  for (let i = 0; i < data.length; i++) {
    const value = data[i];

    if (value < 0x80) {
      str += String.fromCharCode(value);
    } else if (value > 0xBF && value < 0xE0) {
      str += String.fromCharCode((value & 0x1F) << 6 | data[i + 1] & 0x3F);
      i += 1;
    } else if (value > 0xDF && value < 0xF0) {
      str += String.fromCharCode((value & 0x0F) << 12 | (data[i + 1] & 0x3F) << 6 | data[i + 2] & 0x3F);
      i += 2;
    } else {
      // surrogate pair
      const charCode = ((value & 0x07) << 18 | (data[i + 1] & 0x3F) << 12 | (data[i + 2] & 0x3F) << 6 | data[i + 3] & 0x3F) - 0x010000;

      str += String.fromCharCode(charCode >> 10 | 0xD800, charCode & 0x03FF | 0xDC00);
      i += 3;
    }
  }

  return str;
}

const Encode = {
  scramble,
  scrambleWithKey,
  descramble,
  descrambleWithKey,
  hexEncode,
  hexDecode,
  safeIdEncode,
  safeIdDecode,
  fromUTF8,
  toUTF8,
  normalizeHexChars
};

export {
  scramble,
  scrambleWithKey,
  descramble,
  descrambleWithKey,
  hexEncode,
  hexDecode,
  safeIdEncode,
  safeIdDecode,
  fromUTF8,
  toUTF8,
  normalizeHexChars
};

export default Encode;
